<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import { useRequest } from '@/composables/useRequest';
import { useGlobalStore } from '@/stores/global';
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useMediaQuery, useSwipe } from '@vueuse/core';

const globalStore = useGlobalStore();
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  hasTitle,
  hasItems,
  template,
  hasAnyContent,
} = useBlockSafe(props.data);

const autoplay = false;
const autoplayDelay = 3000;
const loop = true;
const swipeThreshold = 50;
const isMobile = useMediaQuery('(max-width: 767px)');
const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
const isLargeDesktop = useMediaQuery('(min-width: 1440px)');
const isMounted = ref(false);
const breakpoint = computed(() => {
  if (!isMounted.value) return 'desktop';
  if (isMobile.value) return 'mobile';
  if (isTablet.value) return 'tablet';
  if (isLargeDesktop.value) return 'large';
  return 'desktop';
});

const activeIndex = ref(0);
const sliderContainer = ref(null);
const sliderWrapper = ref(null);
const autoplayInterval = ref(null);
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0);
useSwipe(sliderContainer, {
  threshold: swipeThreshold,
  onSwipeEnd(direction) {
    if (direction === 'left') nextSlide();
    else if (direction === 'right') prevSlide();
  },
});
const handleResize = () => {
  windowWidth.value = window.innerWidth;
  initializeSliderPosition();
  nextTick(() => {
    scrollToActiveSlide(false);
  });
};
// Mirrors assets/styles/utils/measures.scss's px-to-vw(): scales linearly
// between the reference and max viewport widths, then holds flat beyond
// that, so this JS-driven slider matches the CSS-driven scaling elsewhere.
const FLUID_REFERENCE_WIDTH = 1440;
const FLUID_MAX_WIDTH = 2560;
const FLUID_RATIO = FLUID_MAX_WIDTH / FLUID_REFERENCE_WIDTH;

const getFluidValue = (px, currentWidth) => {
  const maxPx = px * FLUID_RATIO;
  if (currentWidth <= FLUID_REFERENCE_WIDTH) return px;
  if (currentWidth >= FLUID_MAX_WIDTH) return maxPx;
  const slope = (maxPx - px) / (FLUID_MAX_WIDTH - FLUID_REFERENCE_WIDTH);
  return px + slope * (currentWidth - FLUID_REFERENCE_WIDTH);
};

const getAdaptiveValue = (mobile, tablet, desktop, largeBaseValue) => {
  if (breakpoint.value === 'mobile') return mobile;
  if (breakpoint.value === 'tablet') return tablet;
  if (breakpoint.value === 'large') {
    return getFluidValue(largeBaseValue, windowWidth.value);
  }
  return desktop;
};
const gap = computed(() => {
  return getAdaptiveValue(16, 16, 16, 16);
});

const slideHeight = computed(() => {
  return getAdaptiveValue(200, 368, 368, 368);
});

const activeSlideWidth = computed(() => {
  return getAdaptiveValue(200, 368, 368, 368);
});

const inactiveSlideWidth = computed(() => {
  return getAdaptiveValue(95, 184, 184, 184);
});
function getSlideImage(slide) {
  const main = slide?.image?.src ? slide.image : null;

  return {
    src: main?.src || slide?.src || '',
    alt: main?.alt || slide?.alt || '',
    width: main?.width || slide?.width || null,
    height: main?.height || slide?.height || null,
    mobileSrc: main?.src_mobile || slide?.src_mobile || '',
    tabletSrc: main?.src_tablet || slide?.src_tablet || '',
  };
}
const displaySlides = computed(() => {
  if (!isMounted.value || !sliderContainer.value || items.value.length === 0) {
    return items.value.map((slide, index) => ({
      ...slide,
      originalIndex: index,
      resolvedImage: getSlideImage(slide),
    }));
  }

  const containerWidth = sliderContainer.value.clientWidth;
  const currentGap = gap.value;

  let totalWidth = 0;
  items.value.forEach((_, index) => {
    const width =
      index === 0 ? activeSlideWidth.value : inactiveSlideWidth.value;
    totalWidth += width + currentGap;
  });
  totalWidth -= currentGap;

  if (totalWidth >= containerWidth) {
    return items.value.map((slide, index) => ({
      ...slide,
      originalIndex: index,
      resolvedImage: getSlideImage(slide),
    }));
  }

  const result = [];
  let currentWidth = 0;
  let slideIndex = 0;
  let cloneRound = 0;

  while (currentWidth < containerWidth * 1.5) {
    const originalIndex = slideIndex % items.value.length;
    const slide = items.value[originalIndex];

    result.push({
      ...slide,
      originalIndex: originalIndex,
      cloneId: `${originalIndex}-${cloneRound}`,
      resolvedImage: getSlideImage(slide),
    });

    const isActive = result.length - 1 === activeIndex.value;
    const width = isActive ? activeSlideWidth.value : inactiveSlideWidth.value;
    currentWidth += width + currentGap;

    slideIndex++;
    if (slideIndex % items.value.length === 0) {
      cloneRound++;
    }
  }

  return result;
});
const getSlideStyle = (index) => {
  const isActive = index === activeIndex.value;
  const width = isActive ? activeSlideWidth.value : inactiveSlideWidth.value;

  return {
    width: `${width}px`,
    height: `${slideHeight.value}px`,
    flex: `0 0 ${width}px`,
  };
};

const calculateSlidePosition = (slideIndex) => {
  const wrapper = sliderWrapper.value;
  if (!wrapper) return 0;

  const currentGap = gap.value;
  let position = 0;

  for (let i = 0; i < slideIndex; i++) {
    const width =
      i === activeIndex.value
        ? activeSlideWidth.value
        : inactiveSlideWidth.value;
    position += width + currentGap;
  }

  return position;
};

const scrollToActiveSlide = (smooth = true) => {
  const container = sliderContainer.value;
  if (!container) return;

  const containerWidth = container.clientWidth;
  const activeSlidePos = calculateSlidePosition(activeIndex.value);
  const currentActiveWidth = activeSlideWidth.value;

  const scrollPosition =
    activeSlidePos - containerWidth / 2 + currentActiveWidth / 2;

  container.scrollTo({
    left: Math.max(0, scrollPosition),
    behavior: smooth ? 'smooth' : 'auto',
  });
};

const setActiveSlide = (index) => {
  activeIndex.value = index;
  nextTick(() => {
    scrollToActiveSlide();
  });
};

const nextSlide = () => {
  if (activeIndex.value < displaySlides.value.length - 1) {
    setActiveSlide(activeIndex.value + 1);
  } else if (loop) {
    setActiveSlide(0);
  }
};

const prevSlide = () => {
  if (activeIndex.value > 0) {
    setActiveSlide(activeIndex.value - 1);
  } else if (loop) {
    setActiveSlide(displaySlides.value.length - 1);
  }
};

const startAutoplay = () => {
  if (autoplay) {
    autoplayInterval.value = setInterval(() => {
      nextSlide();
    }, autoplayDelay);
  }
};

const stopAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    autoplayInterval.value = null;
  }
};

const handleMouseEnter = () => {
  if (autoplay) {
    stopAutoplay();
  }
};

const handleMouseLeave = () => {
  if (autoplay) {
    startAutoplay();
  }
};

const initializeSliderPosition = () => {
  nextTick(() => {
    setTimeout(() => {
      scrollToActiveSlide();
    }, 50);
  });
};

watch(breakpoint, () => {
  initializeSliderPosition();
});

watch(activeIndex, () => {
  nextTick(() => {
    scrollToActiveSlide();
  });
});

watch(
  displaySlides,
  () => {
    initializeSliderPosition();
  },
  { flush: 'post' },
);

onMounted(() => {
  isMounted.value = true;
  window.addEventListener('resize', handleResize);
  setTimeout(() => {
    initializeSliderPosition();
    startAutoplay();
  }, 100);

  if (sliderContainer.value) {
    sliderContainer.value.addEventListener('mouseenter', handleMouseEnter);
    sliderContainer.value.addEventListener('mouseleave', handleMouseLeave);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  stopAutoplay();

  if (sliderContainer.value) {
    sliderContainer.value.removeEventListener('mouseenter', handleMouseEnter);
    sliderContainer.value.removeEventListener('mouseleave', handleMouseLeave);
  }
});

const catalogOptions = computed(() => {
  if (!props.data.catalog || !Array.isArray(props.data.catalog)) return [];

  return props.data.catalog;
});

const selectedCatalog = ref('');
const selectedTransport = ref('');
const selectedBrand = ref('');
const selectedModel = ref('');
const selectedBodyType = ref('');

const transportOptions = ref([]);
const brandOptions = ref([]);
const modelOptions = ref([]);
const bodyTypeOptions = ref([]);

const currentConfig = computed(() => {
  return (
    props.data.catalog?.find((item) => item.id === selectedCatalog.value) || {}
  );
});

const displayColumns = ref(3);
const calculateActiveColumns = () => {
  const config = props.data.catalog?.find(
    (c) => c.id === selectedCatalog.value,
  );
  if (!config) return 3;

  let count = 0;
  if (config.has_transport_list) count++;
  if (config.has_brand_list) count++;
  if (config.has_model_list) count++;
  if (config.has_bodytype_list) count++;

  return count;
};

const formatData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item) => ({
    label: item.title,
    value: item.slug,
    id: item.id,
  }));
};

const isFormValid = computed(() => {
  const config = currentConfig.value;
  const brandValid = config.has_brand_list ? !!selectedBrand.value : true;
  const modelValid = config.has_model_list ? !!selectedModel.value : true;
  let bodytypeValid = config.has_bodytype_list
    ? !!selectedBodyType.value
    : true;
  if (
    modelValid &&
    (!bodyTypeOptions.value || bodyTypeOptions.value.length === 0)
  ) {
    bodytypeValid = true;
  }

  return brandValid && modelValid && bodytypeValid;
});
function getBrandById(id) {
  const targetId = Number(id);
  const foundBrand = brandOptions.value.find((brand) => brand.id === targetId);
  return foundBrand || null;
}
const findCarUrl = computed(() => {
  if (!isFormValid.value) return null;

  const config = currentConfig.value;
  const basePath = config.slug;
  const currentBrandObj = getBrandById(selectedBrand.value);

  const brandValue = currentBrandObj
    ? currentBrandObj.value
    : selectedBrand.value;
  const segments = [`tm-${brandValue}`];

  if (selectedTransport.value && selectedTransport.value !== 'passenger') {
    segments.unshift(selectedTransport.value);
  }

  if (selectedBodyType.value) segments.push(selectedBodyType.value);
  if (selectedModel.value) segments.push(`m-${selectedModel.value}`);
  return `${basePath}/${segments.join('/')}`;
});

onMounted(() => {
  if (props.data.catalog?.length) {
    const initialCatalog = props.data.catalog[0];
    selectedCatalog.value = initialCatalog.id;

    onCatalogChange(initialCatalog);
  }
});

const isLoading = ref(true);
// Shared across all cascading handlers so a newer selection always invalidates
// any still in-flight response from a previous one, preventing stale data
// (and the mismatched catalog/transport/brand params that follow) from being applied.
const requestSeq = ref(0);

async function onCatalogChange(item) {
  const seq = ++requestSeq.value;
  isLoading.value = true;
  selectedTransport.value = '';
  selectedBrand.value = '';
  selectedModel.value = '';
  selectedBodyType.value = '';

  try {
    displayColumns.value = calculateActiveColumns();
    if (item.has_transport_list) {
      const response = await useRequest(
        `find-car/transport-types?catalog=${item.id}`,
      );
      if (seq !== requestSeq.value) return;
      transportOptions.value = formatData(response.data);
    } else {
      transportOptions.value = [];
    }
    if (!item.has_transport_list && item.has_brand_list) {
      const response = await useRequest(`find-car/brands?catalog=${item.id}`);
      if (seq !== requestSeq.value) return;
      brandOptions.value = formatData(response.data);
    } else {
      brandOptions.value = [];
    }
    modelOptions.value = [];
    bodyTypeOptions.value = [];
  } catch (e) {
    console.error(`Error find-car/brands?catalog=${item.id}`, e);
  } finally {
    if (seq === requestSeq.value) isLoading.value = false;
  }
}
async function onTransportChange() {
  const seq = ++requestSeq.value;
  isLoading.value = true;
  selectedBrand.value = '';
  selectedModel.value = '';
  selectedBodyType.value = '';

  modelOptions.value = [];
  bodyTypeOptions.value = [];
  try {
    const response = await useRequest(
      `find-car/brands?catalog=${selectedCatalog.value}&transport=${selectedTransport.value}`,
    );
    if (seq !== requestSeq.value) return;
    brandOptions.value = formatData(response.data);
  } catch (e) {
    console.error(
      `Error find-car/brands?catalog=${selectedCatalog.value}&transport=${selectedTransport.value}`,
      e,
    );
  } finally {
    if (seq === requestSeq.value) isLoading.value = false;
  }
}

async function onBrandChange() {
  const seq = ++requestSeq.value;
  isLoading.value = true;
  selectedModel.value = '';
  selectedBodyType.value = '';

  bodyTypeOptions.value = [];
  try {
    const response = await useRequest(
      `find-car/models?catalog=${selectedCatalog.value}&transport=${selectedTransport.value}&brand=${selectedBrand.value}`,
    );
    if (seq !== requestSeq.value) return;
    modelOptions.value = formatData(response.data);
  } catch (e) {
    console.error(
      `Error ${selectedCatalog.value}-${selectedTransport.value}-${selectedBrand.value}-models`,
      e,
    );
  } finally {
    if (seq === requestSeq.value) isLoading.value = false;
  }
}

async function onModelChange() {
  const seq = ++requestSeq.value;
  isLoading.value = true;
  selectedBodyType.value = '';
  try {
    const response = await useRequest(
      `find-car/body-types?catalog=${selectedCatalog.value}&brand=${selectedBrand.value}&model=${selectedModel.value}`,
    );
    if (seq !== requestSeq.value) return;
    bodyTypeOptions.value = formatData(response.data);
  } catch (e) {
    console.error(
      `Error ${selectedCatalog.value}-${selectedTransport.value}-${selectedBrand.value}-models`,
      e,
    );
  } finally {
    if (seq === requestSeq.value) isLoading.value = false;
  }
}

</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="find-car">
        <h2
          v-if="hasTitle"
          class="mb-0 find-car__title h2 white text-center"
          data-aos="fade-in"
          data-aos-delay="300"
          v-html="title"
        ></h2>
        <div v-if="hasItems" ref="sliderContainer" class="find-car__media">
          <div ref="sliderWrapper" class="find-car__items">
            <div
              v-for="(slide, index) in displaySlides"
              :key="`${slide.originalIndex}-${index}`"
              class="find-car__item"
              :class="{
                'find-car__item--active': index === activeIndex,
              }"
              :style="getSlideStyle(index)"
              @click="setActiveSlide(index)"
            >
              <picture
                class="find-car__item-image"
                data-aos="fade-left"
                data-aos-delay="500"
              >
                <source
                  v-if="slide.resolvedImage.mobileSrc"
                  media="(max-width: 767px)"
                  :srcset="slide.resolvedImage.mobileSrc"
                />
                <source
                  v-if="slide.resolvedImage.tabletSrc"
                  media="(min-width: 768px) and (max-width: 1023px)"
                  :srcset="slide.resolvedImage.tabletSrc"
                />
                <NuxtImg
                  :src="slide.resolvedImage.src"
                  :alt="slide.resolvedImage.alt"
                  :width="slide.resolvedImage.width"
                  :height="slide.resolvedImage.height"
                  loading="lazy"
                  draggable="false"
                />
              </picture>
            </div>
          </div>
        </div>
        <div class="find-car__form" :data-columns="displayColumns">
          <div
            v-if="isLoading"
            :class="[
              'find-car__form-overlay',
              `find-car__form-overlay--${data?.background_color}`,
            ]"
            class=""
            aria-hidden="true"
          >
            <div class="find-car__form-overlay-spinner"></div>
          </div>
          <div
            v-if="currentConfig.has_transport_list"
            class="find-car__form-group"
          >
            <FloatLabel
              class="find-car__form-floatlabel floatlabel-onblack floatlabel--active"
              variant="in"
            >
              <Select
                v-model="selectedTransport"
                input-id="selectedTransport"
                :options="transportOptions"
                option-label="label"
                option-value="value"
                variant="filled"
                class="select-onblack find-car__form-select"
                size="large"
                overlay-class="overlay-onblack"
                :disabled="isLoading"
                :pt="{ label: { 'aria-label': $t('find_car.transport') } }"
                @change="onTransportChange"
              >
                <template #dropdownicon>
                  <CIcon
                    name="direction-white"
                    width="16"
                    height="16"
                    class="p-select-dropdown__icon"
                  />
                </template>
              </Select>
              <label for="selectedTransport" class="find-car__form-label">{{
                $t('find_car.transport')
              }}</label>
            </FloatLabel>
          </div>
          <div v-if="currentConfig.has_brand_list" class="find-car__form-group">
            <FloatLabel
              :class="[
                'find-car__form-floatlabel floatlabel-onblack',
                !selectedTransport &&
                transportOptions &&
                transportOptions.length > 0
                  ? 'floatlabel--disabled'
                  : 'floatlabel--active',
              ]"
              variant="in"
            >
              <Select
                v-model="selectedBrand"
                input-id="selectedBrand"
                :options="brandOptions"
                option-label="label"
                option-value="id"
                variant="filled"
                class="select-onblack find-car__form-select"
                size="large"
                overlay-class="overlay-onblack"
                :empty-message="$t('find_car.empty_options')"
                :disabled="
                  isLoading ||
                  (!selectedTransport &&
                    transportOptions &&
                    transportOptions.length > 0)
                "
                :pt="{ label: { 'aria-label': $t('find_car.brand') } }"
                @change="onBrandChange"
              >
                <template #dropdownicon>
                  <CIcon
                    name="direction-white"
                    width="16"
                    height="16"
                    class="p-select-dropdown__icon"
                  />
                </template>
              </Select>
              <label for="selectedBrand" class="find-car__form-label">{{
                $t('find_car.brand')
              }}</label>
            </FloatLabel>
          </div>
          <div v-if="currentConfig.has_model_list" class="find-car__form-group">
            <FloatLabel
              :class="[
                'find-car__form-floatlabel floatlabel-onblack',
                !selectedBrand ? 'floatlabel--disabled' : 'floatlabel--active',
              ]"
              variant="in"
            >
              <Select
                v-model="selectedModel"
                input-id="selectedModel"
                :options="modelOptions"
                option-label="label"
                option-value="value"
                variant="filled"
                :disabled="isLoading || !selectedBrand"
                class="select-onblack find-car__form-select"
                size="large"
                overlay-class="overlay-onblack"
                :empty-message="$t('find_car.empty_options')"
                :pt="{ label: { 'aria-label': $t('find_car.model') } }"
                @change="onModelChange"
              >
                <template #dropdownicon>
                  <CIcon
                    name="direction-white"
                    width="16"
                    height="16"
                    class="p-select-dropdown__icon"
                  />
                </template>
              </Select>
              <label for="selectedModel" class="find-car__form-label">{{
                $t('find_car.model')
              }}</label>
            </FloatLabel>
          </div>
          <div
            v-if="currentConfig.has_bodytype_list"
            class="find-car__form-group"
          >
            <FloatLabel
              :class="[
                'find-car__form-floatlabel floatlabel-onblack',
                !selectedModel ? 'floatlabel--disabled' : 'floatlabel--active',
              ]"
              variant="in"
            >
              <Select
                v-model="selectedBodyType"
                input-id="bodyType"
                :options="bodyTypeOptions"
                option-label="label"
                option-value="value"
                variant="filled"
                class="select-onblack find-car__form-select"
                :disabled="isLoading || !selectedModel"
                size="large"
                overlay-class="overlay-onblack"
                :empty-message="$t('find_car.empty_options')"
                :pt="{ label: { 'aria-label': $t('find_car.body_type') } }"
              >
                <template #dropdownicon>
                  <CIcon
                    name="direction-white"
                    width="16"
                    height="16"
                    class="p-select-dropdown__icon"
                  />
                </template>
              </Select>
              <label for="bodyType" class="find-car__form-label">{{
                $t('find_car.body_type')
              }}</label>
            </FloatLabel>
          </div>
          <div class="find-car__form-group find-car__form-group--radio">
            <div
              v-for="catalogItem in catalogOptions"
              :key="catalogItem.slug"
              class="find-car__form-radio"
            >
              <RadioButton
                v-model="selectedCatalog"
                :input-id="catalogItem.id"
                name="condition"
                :value="catalogItem.id"
                class="radio-onblack find-car__form-radio-button"
                :disabled="isLoading"
                @change="onCatalogChange(catalogItem)"
              />
              <label
                :for="catalogItem.id"
                class="find-car__form-radio-label body-3-regular"
                >{{ catalogItem.title }}</label
              >
            </div>
          </div>
          <div class="find-car__form-button">
            <Button
              type="button"
              class="find-car__form-button-item button-large-danger button-large-danger--with-icon link-large js-button-find"
              severity="danger"
              size="large"
              :disabled="!isFormValid"
              @click="findCarUrl && navigateTo(findCarUrl)"
            >
              <CIcon
                name="searсh"
                width="24"
                height="24"
                class="button-large-danger__icon"
              />
              <span>{{ $t('find_car.choose_car') }}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
@keyframes form-spin {
  to {
    transform: rotate(360deg);
  }
}
.find-car {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    justify-items: center;
    width: 100%;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: auto auto;
      justify-content: space-between;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__title {
    margin: 0;
  }
  &__media {
    display: flex;
    overflow-x: hidden;
    overflow-y: hidden;
    position: relative;
    scroll-behavior: smooth;
    scrollbar-width: none;
    touch-action: pan-y pinch-zoom;
    user-select: none;
    //margin: 0 -16px;
    padding: 0 16px;
    //width: calc(100% + 32px);
    -ms-overflow-style: none;
    width: 100%;
    justify-content: center;
    &::-webkit-scrollbar {
      display: none;
    }
    @include media-breakpoint-up(sm) {
      //margin: 0 -20px;
      //padding: 0 20px;
      //width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      //margin: 0 -48px;
      //padding: 0 48px;
      //width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      //margin: 0 px-to-vw(-48);
      //padding: 0 px-to-vw(48);
      //width: calc(100% + px-to-vw(96));
      &:before {
        @include responsive-spacing(height, 1);
      }
    }
  }
  &__items {
    display: flex;
    gap: 16px;
    margin: 0 auto;
    will-change: transform;
    transition: gap 0.3s ease;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__item {
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 4px;
    overflow: hidden;
    transition: all 0.3s ease;
    position: relative;
    height: 200px;
    width: 95px;
    &:before {
      background-color: rgba($white, 0.8);
      border-radius: 4px;
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      transition: all 0.3s ease;
      width: 100%;
    }
    @include media-breakpoint-up(sm) {
      border-radius: 8px;
      height: 368;
      width: 184px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(height, 368);
      @include responsive-spacing(width, 184);
      &:before {
        @include responsive-spacing(border-radius, 8);
      }
    }
    &--active {
      width: 200px;
      &:before {
        opacity: 0;
      }
      @include media-breakpoint-up(sm) {
        width: 368px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 368);
      }
    }
    &-image {
      display: block;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      width: 100%;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    position: relative;
    &-overlay {
      display: flex;
      inset: 0;
      pointer-events: all;
      position: absolute;
      z-index: 5;
      &--white {
        background: rgba($white, 0.65);
      }
      &--gray {
        background: rgba($gray, 0.65);
      }
      &--black {
        background: rgba($black, 0.65);
      }
      &-spinner {
        animation: form-spin 0.8s linear infinite;
        border: 3px solid $divider;
        border-radius: 50%;
        border-top-color: $black;
        height: 40px;
        margin: auto;
        position: sticky;
        width: 40px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-top-width, 3);
          @include responsive-spacing(height, 40);
          @include responsive-spacing(width, 40);
          top: calc(50% - px-to-vw(20));
        }
      }
    }

    &-group {
      background: rgba($surface-container-opacity, 0.5);
      border: 1px solid $on-surface-variant-2;
      border-bottom: unset;
      @include media-breakpoint-up(md) {
        order: 1;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
      }
      &:first-child {
        border-radius: 4px 4px 0 0;
        @include media-breakpoint-up(sm) {
          border-radius: 4px 0 0 0;
          border-right: unset;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-top-left-radius, 4);
        }
      }
      &:nth-child(3) {
        @include media-breakpoint-up(sm) {
          border-radius: 0 4px 0 0;
          border-left: unset;
        }
        @include media-breakpoint-up(md) {
          border-radius: 0;
        }
      }
      &--radio {
        display: flex;
        flex-direction: column;
        padding: 24px 15px 23px;
        @include media-breakpoint-up(sm) {
          flex-direction: row;
          gap: 46px;
          grid-column: span 3;
          min-height: 80px;
          padding: 24px 27px;
          width: 100%;
        }
        @include media-breakpoint-up(md) {
          border-bottom: 1px solid $on-surface-variant-2;
          border-radius: 0 0 4px 4px;
          grid-column: span 4;
          order: 3;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-left-radius, 4);
          @include responsive-spacing(border-bottom-right-radius, 4);
          @include responsive-spacing(border-bottom-width, 1);
          @include responsive-spacing(gap, 46);
          @include responsive-spacing(min-height, 80);
          @include responsive-spacing(padding-bottom, 24);
          @include responsive-spacing(padding-top, 24);
          @include responsive-spacing(padding-left, 27);
          @include responsive-spacing(padding-right, 27);
        }
      }
    }
    &-select {
      width: 100%;
    }
    &-radio {
      align-items: center;
      display: grid;
      gap: 8px;
      grid-template-columns: 16px max-content;
      @include media-breakpoint-up(sm) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
      &-label {
        color: $white;
        text-align: left;
      }
    }
    &-button {
      width: 100%;
      &-item {
        height: 100%;
      }
      @include media-breakpoint-up(sm) {
        grid-column: span 3;
      }
      @include media-breakpoint-up(md) {
        border-right: 1px solid $on-surface-variant-2;
        border-top: 1px solid $on-surface-variant-2;
        border-radius: 0 4px 0 0;
        grid-column: span 1;
        order: 2;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-right-radius, 4);
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(border-right-width, 1);
      }
    }
    &[data-columns='1'] {
      @include media-breakpoint-up(sm) {
        grid-template-columns: 1fr;
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: 8fr 4fr;
      }
      #{$parent} {
        &__form {
          &-group {
            &:first-child {
              @include media-breakpoint-up(sm) {
                border-radius: 4px 4px 0 0;
                border-right: 1px solid $on-surface-variant-2;
              }
              @include media-breakpoint-up(md) {
                border-radius: 4px 0 0 0;
                border-right: unset;
              }
            }
            &--radio {
              @include media-breakpoint-up(md) {
                grid-column: span 2;
              }
            }
          }
          &-button {
          }
        }
      }
    }
    &[data-columns='3'] {
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(3, 1fr);
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(4, 1fr);
      }
    }
    &[data-columns='4'] {
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(2, 1fr);
      }
      @include media-breakpoint-up(lg) {
        grid-template-columns: repeat(4, 1fr) auto;
      }
      #{$parent} {
        &__form {
          &-group {
            &:nth-child(2) {
              @include media-breakpoint-up(sm) {
                border-radius: 0 4px 0 0;
              }
              @include media-breakpoint-up(lg) {
                border-radius: unset;
                border-right: unset;
              }
            }
            &:nth-child(3) {
              @include media-breakpoint-up(sm) {
                border-radius: 0;
                border-left: 1px solid $on-surface-variant-2;
                border-right: unset;
              }
              @include media-breakpoint-up(md) {
                border-radius: 0;
              }
            }
            &--radio {
              @include media-breakpoint-up(sm) {
                grid-column: span 2;
              }
              @include media-breakpoint-up(md) {
                border-bottom: unset;
                border-radius: 0;
                order: 2;
              }
              @include media-breakpoint-up(lg) {
                border-bottom: 1px solid $on-surface-variant-2;
                border-radius: 0 0 4px 4px;
                order: 3;
                grid-column: span 5;
              }
            }
          }
          &-button {
            @include media-breakpoint-up(sm) {
              grid-column: span 2;
            }
            @include media-breakpoint-up(md) {
              border-radius: 0 0 4px 4px;
              border: unset;
              order: 3;
              &:deep(.p-button.button-large-danger) {
                border-radius: 0 0 4px 4px;
              }
            }
            @include media-breakpoint-up(lg) {
              order: 2;
              grid-column: span 1;
              border-right: 1px solid $on-surface-variant-2;
              border-top: 1px solid $on-surface-variant-2;
              border-radius: 0 4px 0 0;
              &:deep(.p-button.button-large-danger) {
                border-radius: 0 4px 0 0;
              }
            }
          }
        }
      }
    }
  }
}
.slide-content-enter-active,
.slide-content-leave-active {
  transition: all 0.3s ease;
}

.slide-content-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.slide-content-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
